import {FormProvider, useForm} from "react-hook-form";
import {useCreateSessionContext} from "../../context/create-session";
import {
  createSessionBasicFormStepFormValidationSchema,
  CreateSessionBasicFormStepFormValues,
} from "./schema-validation";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {useNavigate} from "react-router-dom";
import {CreateSessionOfferingForm} from "../../../../../components/offering-form/session-bundle/session";
import {useOAuthCalendarResume} from "../../../../../utils/google-calendar-resume";
import {OfferingSchedulesGate, useOfferingSchedules} from "@/features/availability";
import {scrollToFirstOfferingFormError} from "../../../../../components/offering-form/accordion/scroll-to-first-error";

/**
 * The schedule list is a precondition of this step, not a detail of one section: it
 * decides the form's `availabilityScheduleId` default, which the Availability accordion
 * section used to fill in on mount — and never did when the coach left it collapsed.
 */
export function CreateSessionBasicFormStep() {
  return (
    <OfferingSchedulesGate>
      <CreateSessionBasicForm />
    </OfferingSchedulesGate>
  );
}

function CreateSessionBasicForm() {
  const navigate = useNavigate();
  const {
    dispatch,
    state: {formData},
  } = useCreateSessionContext();
  const snapshot = useOAuthCalendarResume<CreateSessionBasicFormStepFormValues>();
  const {resolveSelection} = useOfferingSchedules();

  const restoredValues = snapshot ?? formData;

  const formMethods = useForm<CreateSessionBasicFormStepFormValues>({
    resolver: yupResolver(createSessionBasicFormStepFormValidationSchema),
    mode: "onSubmit",
    defaultValues: {
      ...restoredValues,
      // Reconciled here rather than in the Availability section: a restored draft can
      // name a schedule that has since been deleted, and the gate above guarantees the
      // list is loaded by the time this runs.
      availabilityScheduleId: resolveSelection(restoredValues.availabilityScheduleId),
    },
  });
  // console.log({formData});

  function onSubmit(values: CreateSessionBasicFormStepFormValues) {
    // console.log({values});
    dispatch({
      type: "go-to-checkout-form-step",
      payload: values,
    });
  }

  const isFormError = Object.keys(formMethods.formState.errors).length > 0;
  const isGeneralInformationError =
    formMethods.formState.errors.name ||
    formMethods.formState.errors.description ||
    formMethods.formState.errors.coverImage ||
    formMethods.formState.errors.slug ||
    formMethods.formState.errors.callDuration ||
    formMethods.formState.errors.customCallDuration;
  const isPricingError =
    formMethods.formState.errors.callPrice ||
    formMethods.formState.errors.callPriceCurrency ||
    formMethods.formState.errors.callPriceType ||
    formMethods.formState.errors.currencies;
  const isLocationError = formMethods.formState.errors.callLocation;
  const isSettingsError =
    formMethods.formState.errors.hideFromProfile ||
    formMethods.formState.errors.isUrlRedirect ||
    formMethods.formState.errors.requestCustomerPhoneNumber ||
    formMethods.formState.errors.telegramGroupId ||
    formMethods.formState.errors.url_redirect;
  const isAvailabilityError = formMethods.formState.errors.availabilityScheduleId;

  // console.log({watch: formMethods.watch(), errors: formMethods.formState.errors});
  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(onSubmit, scrollToFirstOfferingFormError)}
        autoComplete="off"
      >
        <Space direction="vertical" size={16} style={{display: "flex"}}>
          <CreateSessionOfferingForm
            formType="create"
            isGeneralInformationError={!!isGeneralInformationError}
            isLocationError={!!isLocationError}
            isPricingError={!!isPricingError}
            isSettingsError={!!isSettingsError}
            isAvailabilityError={!!isAvailabilityError}
          />
          {isFormError && (
            <Alert
              message="Please fill in the required fields above, outlined in red"
              type="warning"
            />
          )}

          <Row gutter={[0, 16]}>
            <Col span={24} sm={12}>
              <StyledServiceButtonWrapper>
                <Button
                  onClick={() => {
                    dispatch({
                      type: "reset-state",
                    });
                    navigate("/calls");
                  }}
                  block
                  size="small"
                  type="default"
                >
                  Back
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType={"submit"}
                  size="small"
                  disabled={
                    Object.keys(formMethods.formState.errors).length > 0 ||
                    Object.values(formMethods.watch()).length < 1
                  }
                >
                  Next
                </Button>
              </StyledServiceButtonWrapper>
            </Col>
          </Row>
        </Space>
      </form>
    </FormProvider>
  );
}
